// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { Box, Card, CardContent, CardHeader, Divider, Link as MuiLink, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { User } from "lucide-react";

import { formatIndustry, INDUSTRY_OPTIONS } from "@crm-src/lib/industries";
import { Money } from "@crm-src/components/money";
import { ResellerOnly } from "@crm-src/components/reseller";
import { InlineSalespeopleEditor, InlineSelectEditor, type InlineSelectOption } from "@crm-src/components/inline-edit";

import type { DetailedLead, LeadPriority } from "@crm-src/types/lead";
import { LEAD_PRIORITIES } from "@crm-src/types/lead";

// Priority pill accent — urgent reads error (red), high reads warning
// (orange), medium reads primary (blue), everything else stays neutral.
function getPriorityColor(priority: string): "error" | "warning" | "primary" | "neutral" {
  if (priority === "urgent") return "error";
  if (priority === "high") return "warning";
  if (priority === "medium") return "primary";
  return "neutral";
}

/**
 * The "Overview" tab of the prospect detail page — Business Overview,
 * address/timezone, and the expected-value/ports summary. Extracted from
 * the old single-card layout; identity (avatar / name / id) now lives in
 * the persistent page header and contacts moved to their own tab.
 */
export function ProspectOverviewTab({
  prospect,
  editable = false,
  onAssignSalespeople,
  onChangePriority,
  onChangeIndustry,
  onChangeReseller,
  resellerOptions,
}: {
  prospect: DetailedLead;
  /**
   * When true (and the matching handler is supplied), the Priority and
   * Salesperson rows become inline-editable in place — same primitives as the
   * list page, gated by the caller (super_admin). Defaults to read-only so
   * existing render-only callers and tests are unaffected.
   * See docs/crm.md → "Inline editing".
   */
  editable?: boolean;
  onAssignSalespeople?: (userIds: string[]) => Promise<unknown>;
  onChangePriority?: (value: LeadPriority) => Promise<unknown>;
  onChangeIndustry?: (value: string) => Promise<unknown>;
  /** Reseller options ("none" === Direct) — supplied by the detail page's useResellers(). */
  resellerOptions?: InlineSelectOption[];
  onChangeReseller?: (resellerId: string) => Promise<unknown>;
}) {
  const priorityColor = prospect.priority ? getPriorityColor(prospect.priority) : "neutral";
  return (
    <Card>
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <User size={20} />
            <Typography component="span" sx={{ fontWeight: 600 }}>
              Prospect Information
            </Typography>
          </Box>
        }
      />
      <CardContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Business Overview */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography component="h4" sx={{ fontWeight: 500, fontSize: 14, color: "text.primary" }}>
              Business Overview
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {/* Priority */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                  Priority
                </Typography>
                <InlineSelectEditor
                  canEdit={editable && Boolean(onChangePriority)}
                  value={prospect.priority ?? ""}
                  options={LEAD_PRIORITIES.map((p) => ({ value: p.value, label: p.label }))}
                  onSave={(v) => onChangePriority!(v as LeadPriority)}
                  ariaLabel="Edit priority"
                  errorMessage="Failed to update priority"
                  variant="panel"
                >
                  {prospect.priority ? (
                    <Box
                      component="span"
                      sx={{
                        px: 1,
                        py: 0.5,
                        fontSize: 12,
                        borderRadius: 5,
                        ...(priorityColor === "neutral"
                          ? {
                              bgcolor: (t) => alpha(t.palette.text.primary, 0.08),
                              color: "text.primary",
                            }
                          : {
                              bgcolor: (t) => alpha(t.palette[priorityColor].main, 0.16),
                              color: `${priorityColor}.dark`,
                            }),
                      }}
                    >
                      {prospect.priority} priority
                    </Box>
                  ) : (
                    <Typography component="span" sx={{ fontSize: 14, color: "text.disabled" }}>
                      Not set
                    </Typography>
                  )}
                </InlineSelectEditor>
              </Box>

              {/* Prospect Source */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                  Prospect Source
                </Typography>
                <Typography component="span" sx={{ fontSize: 14, fontWeight: 500 }}>
                  {prospect.source || "Not specified"}
                </Typography>
              </Box>

              {/* Industry */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                  Industry
                </Typography>
                <InlineSelectEditor
                  canEdit={editable && Boolean(onChangeIndustry)}
                  value={prospect.industry ?? ""}
                  options={INDUSTRY_OPTIONS}
                  onSave={(v) => onChangeIndustry!(v)}
                  ariaLabel="Edit industry"
                  errorMessage="Failed to update industry"
                  variant="panel"
                >
                  {prospect.industry ? (
                    <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
                      {formatIndustry(prospect.industry)}
                    </Typography>
                  ) : (
                    <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "text.disabled" }}>
                      Not set
                    </Typography>
                  )}
                </InlineSelectEditor>
              </Box>

              {/* Reseller wall: the whole Reseller row disappears for a viewer
                  without crm:resellers. See components/reseller.tsx. */}
              <ResellerOnly>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                    Reseller
                  </Typography>
                  <Box>
                    <InlineSelectEditor
                      canEdit={editable && Boolean(onChangeReseller) && Boolean(resellerOptions)}
                      value={prospect.reseller ? String(prospect.reseller.id) : "none"}
                      options={resellerOptions ?? []}
                      onSave={(v) => onChangeReseller!(v)}
                      ariaLabel="Edit reseller"
                      errorMessage="Failed to update reseller"
                      variant="panel"
                    >
                      {prospect.reseller ? (
                        <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
                          {prospect.reseller.name}
                        </Typography>
                      ) : (
                        <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "text.disabled" }}>
                          Direct
                        </Typography>
                      )}
                    </InlineSelectEditor>
                  </Box>
                </Box>
              </ResellerOnly>

              {/* Dealers */}
              {prospect.dealers && prospect.dealers.length > 0 && (
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                  <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                    Dealers
                  </Typography>
                  <Box sx={{ textAlign: "right", maxWidth: 300 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      {prospect.dealers.map((dealer) => {
                        const fullAddress = [
                          dealer.address,
                          dealer.city,
                          dealer.state,
                          dealer.postal_code,
                          dealer.country,
                        ]
                          .filter(Boolean)
                          .join(", ");
                        return (
                          <Box key={dealer.id} sx={{ fontSize: 14 }}>
                            <Box component="span" sx={{ fontWeight: 500, color: "text.primary" }}>
                              {dealer.name}
                            </Box>
                            {fullAddress && (
                              <Box sx={{ fontSize: 12, color: "text.disabled", mt: 0.25 }}>
                                {fullAddress}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Salesperson */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                  Salesperson
                </Typography>
                <Box>
                  <InlineSalespeopleEditor
                    canEdit={editable && Boolean(onAssignSalespeople)}
                    value={(prospect.assigned_salespeople ?? []).map((s) => s.user_id)}
                    onSave={(ids) => onAssignSalespeople!(ids)}
                    variant="panel"
                  >
                    {prospect.assigned_salespeople &&
                    prospect.assigned_salespeople.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {prospect.assigned_salespeople.map((salesperson) => (
                          <Box
                            key={salesperson.id}
                            component="span"
                            sx={{
                              fontSize: 12,
                              fontWeight: 500,
                              color: "text.primary",
                              bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                            }}
                          >
                            {salesperson.full_name}
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "text.disabled" }}>
                        Not assigned
                      </Typography>
                    )}
                  </InlineSalespeopleEditor>
                </Box>
              </Box>

              {/* Product Information */}
              {((prospect.product &&
                prospect.product.name &&
                prospect.product.name.toLowerCase() !== "none") ||
                (prospect.product_feature &&
                  prospect.product_feature.name &&
                  prospect.product_feature.name.toLowerCase() !== "none")) && (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                    Product
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                    {prospect.product &&
                      prospect.product.name &&
                      prospect.product.name.toLowerCase() !== "none" && (
                        <Box
                          component="span"
                          sx={{
                            fontSize: 12,
                            fontWeight: 500,
                            color: "primary.dark",
                            bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 1,
                          }}
                        >
                          {prospect.product.name}
                        </Box>
                      )}
                    {prospect.product_feature &&
                      prospect.product_feature.name &&
                      prospect.product_feature.name.toLowerCase() !== "none" && (
                        <Box
                          component="span"
                          sx={{
                            fontSize: 12,
                            fontWeight: 500,
                            color: "success.dark",
                            bgcolor: (t) => alpha(t.palette.success.main, 0.12),
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 1,
                          }}
                        >
                          {prospect.product_feature.name}
                        </Box>
                      )}
                  </Box>
                </Box>
              )}

              {/* Separator between important info and additional details */}
              <Divider sx={{ my: 2 }} />

              {/* Website */}
              {prospect.website && (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                    Website
                  </Typography>
                  <MuiLink
                    href={
                      prospect.website.startsWith("http://") ||
                      prospect.website.startsWith("https://")
                        ? prospect.website
                        : `https://${prospect.website}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    title={prospect.website}
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: "primary.main",
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      textDecoration: "none",
                      "&:hover": { color: "primary.dark", textDecoration: "underline" },
                    }}
                  >
                    {prospect.website
                      .replace(/^https?:\/\//, "")
                      .replace(/^www\./, "")}
                  </MuiLink>
                </Box>
              )}

              {/* Address Information */}
              {prospect.address ||
              prospect.city ||
              prospect.state ||
              prospect.country ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                    Address
                  </Typography>
                  <Box sx={{ textAlign: "right", maxWidth: 300 }}>
                    <Box sx={{ fontSize: 14, color: "text.secondary" }}>
                      {(() => {
                        const parts = [];
                        if (prospect.address) parts.push(prospect.address);
                        if (prospect.city) parts.push(prospect.city);
                        if (prospect.state) parts.push(prospect.state);
                        if (prospect.country) parts.push(prospect.country);
                        return parts.join(", ");
                      })()}
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ bgcolor: "surfaceSoft", borderRadius: 1, p: 2, fontSize: 14, color: "text.secondary" }}>
                  Address not provided
                </Box>
              )}

              {/* Timezone Information */}
              {prospect.timezone && (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                    Local Time
                  </Typography>
                  <Box sx={{ textAlign: "right" }}>
                    <Box sx={{ fontSize: 14, color: "text.secondary" }}>
                      {(() => {
                        try {
                          const now = new Date();
                          const timeInTimezone = now.toLocaleString("en-US", {
                            timeZone: prospect.timezone,
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          });
                          return `${timeInTimezone} (${prospect.timezone})`;
                        } catch (error) {
                          console.error("Error formatting timezone:", error);
                          return `Current time (${prospect.timezone})`;
                        }
                      })()}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Financial Overview */}
            {prospect.prospected_value || prospect.prospected_ports ? (
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                {/* Financial wall: redact the money figure (→ "—") for viewers
                    without financial access; the adjacent non-money "Expected
                    Ports" tile keeps the grid balanced, so we redact in place
                    rather than dropping the tile. See components/money.tsx. */}
                <Box sx={{ textAlign: "center", p: 1.5, bgcolor: (t) => alpha(t.palette.success.main, 0.1), borderRadius: 1 }}>
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Expected Value</Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 700, color: "success.main" }}>
                    <Money value={prospect.prospected_value || 0} />
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "center", p: 1.5, bgcolor: (t) => alpha(t.palette.secondary.main, 0.14), borderRadius: 1 }}>
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Expected Ports</Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 700, color: "secondary.dark" }}>
                    {prospect.prospected_ports || 0}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ textAlign: "center", py: 1.5, color: "text.disabled" }}>
                <Typography sx={{ fontSize: 14 }}>No financial data available</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
